import classNames from "classnames";
import React, { useEffect, useRef } from "react";
import styles from "./AnimatedShieldLogo.scss";

const SHIELD_PATH =
    "M394.514 78.506c0-.002 0-.003-4.993.263l4.993-.266-.176-3.316-3.125-1.123-188.521-67.77L201 5.688l-1.691.608L10.788 74.064l-3.125 1.123-.177 3.316 4.993.266a827.375 827.375 0 0 0-4.993-.263v.007l-.002.025-.005.096-.02.377-.07 1.464a942.29 942.29 0 0 0-.25 5.595c-.203 4.849-.465 11.847-.702 20.459-.475 17.219-.851 40.907-.459 66.771.782 51.44 4.604 112.439 17.188 147.888 12.727 35.852 41.295 66.995 74.136 91.553 32.897 24.599 70.547 42.941 102.18 53.023l1.518.484 1.518-.484c31.633-10.082 69.283-28.424 102.18-53.023 32.842-24.558 61.409-55.701 74.136-91.553 12.584-35.449 16.407-96.448 17.188-147.888.393-25.864.016-49.552-.459-66.771-.237-8.612-.499-15.61-.701-20.46a930.218 930.218 0 0 0-.322-7.058l-.019-.377-.005-.096-.001-.025-.001-.007Z";

const VIEWBOX_WIDTH = 402;
const MAX_TILT = 22; // degrees
const ENTRY_ANIMATION_MS = 1900;

/**
 * Animated Vessel Plus shield. The two halves slide in, the vessel pieces pop up,
 * and afterwards the shield tilts and catches light following the pointer.
 */
export const AnimatedShieldLogo = ({ className }) => {
    const svgRef = useRef(null);
    const shineLeftRef = useRef(null);
    const shineRightRef = useRef(null);
    const shinePathLeftRef = useRef(null);
    const shinePathRightRef = useRef(null);
    const shadeLeftRef = useRef(null);
    const shadeRightRef = useRef(null);
    const sheenRef = useRef(null);

    useEffect(() => {
        const svg = svgRef.current;
        if (!svg) {
            return;
        }
        const shineLeft = shineLeftRef.current;
        const shineRight = shineRightRef.current;
        const shinePathLeft = shinePathLeftRef.current;
        const shinePathRight = shinePathRightRef.current;
        const shadeLeft = shadeLeftRef.current;
        const shadeRight = shadeRightRef.current;
        const sheen = sheenRef.current;
        let frame = 0;

        // dx, dy: light direction in shield units relative to the centre
        const applyLight = (dx, dy) => {
            const length = Math.hypot(dx, dy) || 1;
            const nx = dx / Math.max(length, 260);
            const ny = dy / Math.max(length, 260);
            // Each face is tilted outwards from the centre crease
            const faceLeft = Math.max(0, Math.min(1, 0.5 - nx * 0.9));
            const faceRight = Math.max(0, Math.min(1, 0.5 + nx * 0.9));
            shinePathLeft.style.fillOpacity = String(0.05 + faceLeft * 0.5);
            shinePathRight.style.fillOpacity = String(0.05 + faceRight * 0.5);
            // The face turned away from the light gets slightly darker
            shadeLeft.style.fillOpacity = String(Math.max(0, faceRight - faceLeft) * 0.13);
            shadeRight.style.fillOpacity = String(Math.max(0, faceLeft - faceRight) * 0.13);
            const cy = 236 + ny * 220;
            shineLeft.setAttribute("cx", String(100 + nx * 90));
            shineLeft.setAttribute("cy", String(cy));
            shineRight.setAttribute("cx", String(302 + nx * 90));
            shineRight.setAttribute("cy", String(cy));
        };

        const resetLight = () => {
            [sheen, shinePathLeft, shinePathRight, shadeLeft, shadeRight].forEach((element) => {
                element.style.fillOpacity = "";
            });
            svg.style.transform = "";
        };

        // Only react to the pointer once the entry animation has finished
        const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        let active = reduceMotion;
        const startTimer = setTimeout(() => {
            active = true;
        }, ENTRY_ANIMATION_MS);

        const onPointerMove = (event) => {
            if (!active) {
                return;
            }
            cancelAnimationFrame(frame);
            frame = requestAnimationFrame(() => {
                const rect = svg.getBoundingClientRect();
                const scale = VIEWBOX_WIDTH / rect.width;
                const px = event.clientX - (rect.left + rect.width / 2);
                const py = event.clientY - (rect.top + rect.height / 2);
                const radius = 200 + rect.width / 2;
                // Outside the active zone: release and return to the original look
                if (Math.hypot(px, py) > radius) {
                    resetLight();
                    return;
                }
                sheen.style.fillOpacity = "0";
                applyLight(px * scale, py * scale);
                if (!reduceMotion) {
                    const tilt = (value) => Math.max(-1, Math.min(1, value / radius)) * MAX_TILT;
                    svg.style.transform = `perspective(260px) rotateY(${-tilt(px)}deg) rotateX(${tilt(py)}deg)`;
                }
            });
        };

        window.addEventListener("pointermove", onPointerMove, { passive: true });
        return () => {
            clearTimeout(startTimer);
            cancelAnimationFrame(frame);
            window.removeEventListener("pointermove", onPointerMove);
        };
    }, []);

    return (
        <svg
            ref={svgRef}
            className={classNames(styles.base, className)}
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 402 472"
            aria-hidden="true"
            focusable="false"
        >
            <clipPath id="animatedShieldLeftHalf">
                <rect x="0" y="0" width="201" height="472" />
            </clipPath>
            <clipPath id="animatedShieldRightHalf">
                <rect x="201" y="0" width="201" height="472" />
            </clipPath>
            <g className={classNames(styles.shieldHalf, styles.shieldHalfLeft)} clipPath="url(#animatedShieldLeftHalf)">
                <path fill="currentColor" d={SHIELD_PATH} />
                <path ref={shadeLeftRef} className={styles.shieldShade} fill="#000" fillOpacity="0" d={SHIELD_PATH} />
                <path
                    ref={shinePathLeftRef}
                    className={styles.shieldShine}
                    fill="url(#animatedShieldShineL)"
                    fillOpacity="0"
                    d={SHIELD_PATH}
                />
            </g>
            <g className={classNames(styles.shieldHalf, styles.shieldHalfRight)}>
                <g clipPath="url(#animatedShieldRightHalf)">
                    <path fill="currentColor" d={SHIELD_PATH} />
                    <path
                        ref={shadeRightRef}
                        className={styles.shieldShade}
                        fill="#000"
                        fillOpacity="0"
                        d={SHIELD_PATH}
                    />
                    <path
                        ref={shinePathRightRef}
                        className={styles.shieldShine}
                        fill="url(#animatedShieldShineR)"
                        fillOpacity="0"
                        d={SHIELD_PATH}
                    />
                    <path
                        ref={sheenRef}
                        className={styles.shieldShine}
                        fill="url(#animatedShieldSheen)"
                        fillOpacity=".2"
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="m201 11 188.521 67.77s9.136 171.63-15.399 240.745C349.587 388.631 263.248 441.161 201 461V11Z"
                    />
                </g>
            </g>
            <mask
                id="animatedShieldInner"
                width="383"
                height="385"
                x="16"
                y="37"
                maskUnits="userSpaceOnUse"
                style={{ maskType: "luminance" }}
            >
                <path
                    fill="#fff"
                    fillRule="evenodd"
                    d="M77.984 422H323.79L399 37 16 43.685 77.984 422Z"
                    clipRule="evenodd"
                />
            </mask>
            <g fill="#fff" fillRule="evenodd" clipRule="evenodd" mask="url(#animatedShieldInner)">
                <path
                    className={classNames(styles.vesselPiece, styles.vesselPiece1)}
                    d="m188.729 172 13.128 167.576v32.065l-62.063-30.473S133.215 267.124 81 208.409C158.143 182.857 188.729 172 188.729 172Z"
                />
                <path
                    className={classNames(styles.vesselPiece, styles.vesselPiece2)}
                    d="m212 190.5-10.857 168.289v31.852l62.063-30.473s6.579-74.044 58.794-132.759C244.857 201.857 212 190.5 212 190.5Z"
                />
                <path
                    className={classNames(styles.vesselPiece, styles.vesselPiece3)}
                    d="M201.7 137.227l-71.606 29.759 10.897-52.03 60.709-24.58v46.851Z"
                />
                <path
                    className={classNames(styles.vesselPiece, styles.vesselPiece4)}
                    d="m201.257 155.903 71.739 29.67-10.917-51.874-60.822-24.506v46.71Z"
                />
            </g>
            <defs>
                <linearGradient
                    id="animatedShieldSheen"
                    x1="296.093"
                    x2="440.868"
                    y1="11"
                    y2="380.609"
                    gradientUnits="userSpaceOnUse"
                >
                    <stop stopColor="#fff" />
                    <stop offset="1" stopColor="#fff" stopOpacity="0" />
                </linearGradient>
                <radialGradient
                    ref={shineLeftRef}
                    id="animatedShieldShineL"
                    gradientUnits="userSpaceOnUse"
                    cx="100"
                    cy="120"
                    r="300"
                >
                    <stop stopColor="#fff" />
                    <stop offset="0.55" stopColor="#fff" stopOpacity="0.35" />
                    <stop offset="1" stopColor="#fff" stopOpacity="0" />
                </radialGradient>
                <radialGradient
                    ref={shineRightRef}
                    id="animatedShieldShineR"
                    gradientUnits="userSpaceOnUse"
                    cx="302"
                    cy="120"
                    r="300"
                >
                    <stop stopColor="#fff" />
                    <stop offset="0.55" stopColor="#fff" stopOpacity="0.35" />
                    <stop offset="1" stopColor="#fff" stopOpacity="0" />
                </radialGradient>
            </defs>
        </svg>
    );
};
